import React, { useEffect } from 'react';

import {
  Button,
  Divider,
  Flex,
  Group,
  Modal,
  NumberInput,
  Progress,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useUpdateCreditDispensation from 'hooks/creditDispensation/useUpdateCreditDispensation';
import { CreditDispensation } from 'types';
import notificationHandler from 'utils/notifications';

interface IModalCreditDispensationEditProps {
  opened: boolean;
  handleClose: () => void;
  creditDispensation: CreditDispensation | null;
}

interface FormValues {
  creditDispensationId: string;
  credits: number | null;
  dispenseDate: Date;
  expirationDate?: Date | null;
}

const getInitialExpirationDate = (creditDispensation: CreditDispensation | null): Date | null => {
  if (!creditDispensation) {
    return null;
  }
  if (!creditDispensation.expirationDate) {
    return null;
  }

  const shouldUseCancellationDate =
    !creditDispensation.isRollover &&
    creditDispensation.athletePackageCancellationDate &&
    dayjs(creditDispensation.athletePackageCancellationDate) < dayjs(creditDispensation.expirationDate);
  if (shouldUseCancellationDate) {
    return dayjs(creditDispensation.athletePackageCancellationDate).toDate();
  }

  return dayjs(creditDispensation.expirationDate).toDate();
};

const ModalCreditDispensationEdit = ({
  opened,
  creditDispensation,
  handleClose,
}: IModalCreditDispensationEditProps): JSX.Element => {
  const { dateFormat } = useLocalization();

  const form = useForm<FormValues>({
    initialValues: {
      creditDispensationId: creditDispensation?.creditDispensationId || '',
      credits: creditDispensation?.credits || null,
      dispenseDate: creditDispensation?.dispenseDate
        ? dayjs(creditDispensation.dispenseDate).toDate()
        : new Date(),
      expirationDate: getInitialExpirationDate(creditDispensation),
    },
  });

  const closeHandler = (): void => {
    form.reset();
    handleClose();
  };

  const updateMutation = useUpdateCreditDispensation();
  const { successNotification, errorNotification } = notificationHandler();

  const handleExpirationDateChange = (date: Date): void => {
    form.setFieldValue('expirationDate', date);
  };

  const handleEdit = (): void => {
    if (creditDispensation) {
      const payload = {
        ...creditDispensation,
        credits: creditDispensation?.credits !== null ? form.values.credits : null,
        dispenseDate: dayjs(form.values.dispenseDate).format('YYYY-MM-DD'),
        expirationDate: creditDispensation.expirationDate
          ? dayjs(form.values.expirationDate).format('YYYY-MM-DD')
          : null,
      };
      updateMutation.mutate(payload, {
        onError: error => {
          errorNotification('Credits available changed failed', error.response?.data.message);
        },
        onSuccess: () => {
          successNotification('Credit dispensation updated successfuly');
          closeHandler();
        },
      });
    }
  };

  useEffect(() => {
    if (creditDispensation) {
      form.setFieldValue('credits', creditDispensation?.credits || 0);
      form.setFieldValue('dispenseDate', dayjs(creditDispensation.dispenseDate).toDate());
      form.setFieldValue('expirationDate', getInitialExpirationDate(creditDispensation));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditDispensation, opened]);

  return (
    <Modal
      centered
      opened={opened}
      onClose={closeHandler}
      title={<Title order={4}>Edit Credit Dispensation</Title>}
    >
      <Stack>
        <Divider mx='-md' />
        <Group grow>
          <Text fs='italic' c='dimmed' fz='xs' ta='right'>
            Validity Period
          </Text>
          <DatePickerInput
            value={form.values.dispenseDate}
            valueFormat={dateFormat}
            size='sm'
            disabled={true}
          />
          {creditDispensation?.expirationDate ? (
            <DatePickerInput
              {...form.getInputProps('expirationDate')}
              minDate={form.values.dispenseDate}
              maxDate={
                // Max date is athletePackageCancellationDate if not rollover and athletePackage is canceled else none
                !creditDispensation.isRollover && creditDispensation.athletePackageCancellationDate
                  ? dayjs(creditDispensation.athletePackageCancellationDate).toDate()
                  : undefined
              }
              onChange={handleExpirationDateChange}
              disabled={creditDispensation?.hasRolledOver}
              valueFormat={dateFormat}
              size='sm'
              dropdownType='modal'
              modalProps={{ centered: true }}
            />
          ) : (
            <Text fz='sm' c='dimmed'>
              Does not expire
            </Text>
          )}
        </Group>
        <Group noWrap grow>
          <Text fs='italic' c='dimmed' fz='xs' ta='right'>
            Credit Usage
          </Text>

          <Progress
            value={((creditDispensation?.creditsUsed || 0) / (creditDispensation?.credits || 1)) * 100}
            color='kiloBlue'
            size='xl'
            radius={100}
          />
          <Flex align='center' gap='xs' columnGap='xs'>
            <Text>{creditDispensation?.creditsUsed} /</Text>
            {creditDispensation?.credits === null ? (
              <Text>∞</Text>
            ) : (
              <NumberInput
                sx={{ width: '50%' }}
                value={form.values.credits || 0}
                onChange={value => form.setFieldValue('credits', Number(value))}
                required
                size='sm'
                min={creditDispensation?.creditsUsed || 0}
                disabled={creditDispensation?.credits === null || creditDispensation?.hasRolledOver}
              />
            )}
          </Flex>
        </Group>
        <Divider mx='-md' />
        <Group position='right'>
          <Button variant='subtle' onClick={closeHandler}>
            Cancel
          </Button>
          <Button
            onClick={handleEdit}
            loading={updateMutation.isLoading}
            disabled={creditDispensation?.hasRolledOver}
          >
            Confirm
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ModalCreditDispensationEdit;
